import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {AlertTriangle, CheckCircle2, Calendar, ArrowRight, Sun, Waves, Mountain, Bug} from 'lucide-react';
import BookingModal from '../components/BookingModal';

const LatinAmerica = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const healthRisks = [
    {
      icon: <Bug className="w-6 h-6" />,
      title: "Mosquito-Borne Illnesses",
      desc: "Dengue, Zika, and chikungunya in tropical and coastal regions."
    },
    {
      icon: <Waves className="w-6 h-6" />,
      title: "Travellers' Diarrhea",
      desc: "Common from contaminated food and water, especially at resorts."
    },
    {
      icon: <Sun className="w-6 h-6" />,
      title: "Sun & Altitude",
      desc: "Sunburn, dehydration, and altitude sickness in highland destinations."
    }
  ];

  const vaccinesDiscuss = [
    { name: "Hepatitis A", priority: "Highly Recommended", note: "Essential for all travellers to the region" },
    { name: "Typhoid", priority: "Recommended", note: "Especially for those eating outside major resorts" },
    { name: "Yellow Fever", priority: "Required for Some Areas", note: "Mandatory for certain countries (e.g., parts of Brazil, Peru, Colombia)" },
    { name: "Hepatitis B", priority: "Recommended", note: "For extended stays or potential medical procedures" },
    { name: "Rabies (Pre-Exposure)", priority: "Consider for Adventure Travel", note: "Recommended for eco-tourism and rural areas" },
    { name: "Measles/Mumps/Rubella (MMR)", priority: "Ensure Up-to-Date", note: "Measles outbreaks can occur" },
    { name: "Tetanus/Diphtheria/Pertussis", priority: "Ensure Up-to-Date", note: "Booster every 10 years" },
    { name: "COVID-19", priority: "Recommended", note: "Check country-specific entry requirements" }
  ];

  const medications = [
    "Travellers' diarrhea treatment kit (antibiotics like azithromycin)",
    "Oral rehydration salts for dehydration",
    "Insect repellent with DEET (30-50%) or picaridin",
    "Altitude sickness medication (acetazolamide) if visiting high elevations",
    "Broad-spectrum sunscreen (SPF 50+)",
    "Motion sickness medication for boat trips and winding roads"
  ];

  const destinations = [
    { 
      region: "Mexico & Central America",
      countries: ["Mexico", "Costa Rica", "Belize", "Guatemala", "Panama"],
      risks: ["Dengue", "Zika", "Typhoid"]
    },
    { 
      region: "Caribbean Islands",
      countries: ["Dominican Republic", "Jamaica", "Cuba", "Puerto Rico"],
      risks: ["Dengue", "Chikungunya", "Travellers' Diarrhea"]
    },
    { 
      region: "South America",
      countries: ["Brazil", "Peru", "Colombia", "Argentina", "Chile"],
      risks: ["Yellow Fever*", "Malaria*", "Altitude Sickness", "Typhoid"]
    }
  ];

  const travelTips = [
    {
      icon: <Bug className="w-5 h-5" />,
      tip: "Mosquito Protection",
      detail: "Apply repellent regularly, especially dawn/dusk. Wear light-colored long sleeves."
    },
    {
      icon: <Waves className="w-5 h-5" />,
      tip: "Water Safety",
      detail: "Stick to bottled water. Avoid ice, raw vegetables, and street food at unvetted stands."
    },
    {
      icon: <Sun className="w-5 h-5" />,
      tip: "Sun Protection",
      detail: "Reapply sunscreen every 2 hours. Stay hydrated in tropical heat."
    },
    {
      icon: <Mountain className="w-5 h-5" />,
      tip: "Altitude Awareness",
      detail: "Ascend gradually. Rest for 24-48 hours before strenuous activity at high altitude."
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-navy-900">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-purple-600 via-pink-600 to-rose-600 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" />
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/1450353/pexels-photo-1450353.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Latin America & Caribbean"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <div className="inline-block px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm font-medium mb-6">
              Resort Travel • Adventure Tourism • Beach Destinations
            </div>
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-white mb-6">
              Latin America & Caribbean Travel Health
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              From Caribbean beaches to Andean peaks, get expert health advice for your tropical getaway or adventure expedition.
            </p>
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-purple-600 text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Book Latin America Consultation
              <ArrowRight size={20} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Destinations Covered */}
      <section className="py-16 bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-heading font-bold text-navy-900 dark:text-white mb-8 text-center">
            Regions We Cover
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {destinations.map((dest, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-gradient-to-br from-gray-50 to-white dark:from-navy-800 dark:to-navy-900 rounded-2xl border border-gray-100 dark:border-white/5 shadow-lg"
              >
                <h4 className="text-xl font-bold text-navy-900 dark:text-white mb-3">{dest.region}</h4>
                <div className="mb-4">
                  {dest.countries.map((country, i) => (
                    <span key={i} className="inline-block text-sm text-gray-600 dark:text-gray-400 mr-2">
                      {country}{i < dest.countries.length - 1 ? ' •' : ''}
                    </span>
                  ))}
                </div>
                <div className="pt-3 border-t border-gray-200 dark:border-white/10">
                  <div className="text-xs font-bold text-gray-500 dark:text-gray-500 uppercase mb-2">Common Risks</div>
                  <div className="flex flex-wrap gap-2">
                    {dest.risks.map((risk, i) => (
                      <span key={i} className="px-2 py-1 rounded-full bg-purple-100 dark:bg-purple-900/20 text-xs text-purple-700 dark:text-purple-300">
                        {risk}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 dark:text-gray-500 mt-6">
            * Yellow Fever vaccination may be required for entry to certain countries or regions. Malaria risk varies by area.
          </p>
        </div>
      </section>

      {/* Key Health Risks */}
      <section className="py-20 bg-gray-50 dark:bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-4">
              Key Health Risks to Discuss
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Latin America and the Caribbean present diverse health challenges depending on your destination and activities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {healthRisks.map((risk, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-white dark:bg-navy-900 rounded-2xl shadow-lg border border-gray-100 dark:border-white/5"
              >
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-xl flex items-center justify-center text-purple-600 dark:text-purple-400 mb-4">
                  {risk.icon}
                </div>
                <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-3">{risk.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{risk.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vaccines & Medications */}
      <section className="py-20 bg-white dark:bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Vaccines */}
            <div>
              <h2 className="text-3xl font-heading font-bold text-navy-900 dark:text-white mb-6">
                Vaccines to Discuss with Your Clinician
              </h2>
              <div className="space-y-4">
                {vaccinesDiscuss.map((vaccine, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 bg-gray-50 dark:bg-navy-800 rounded-xl border border-gray-100 dark:border-white/5 hover:border-cyan-500/30 transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-bold text-navy-900 dark:text-white">{vaccine.name}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${
                        vaccine.priority.includes("Highly") 
                          ? "bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400"
                          : vaccine.priority.includes("Required")
                          ? "bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400"
                          : vaccine.priority.includes("Recommended")
                          ? "bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }`}>
                        {vaccine.priority}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{vaccine.note}</p>
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-xl">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-900 dark:text-yellow-100">
                    <strong>Yellow Fever Note:</strong> Required for certain regions in Brazil, Peru, Colombia, and other countries. Some countries require proof if arriving from endemic areas.
                  </div>
                </div>
              </div>
            </div>

            {/* Medications & Tips */}
            <div>
              <h2 className="text-3xl font-heading font-bold text-navy-900 dark:text-white mb-6">
                Medications & Travel Supplies
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Essential items for Latin America and Caribbean travel:
              </p>
              <ul className="space-y-3 mb-8">
                {medications.map((med, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-purple-600 dark:text-purple-400 shrink-0 mt-0.5" />
                    <span className="text-gray-700 dark:text-gray-300">{med}</span>
                  </motion.li>
                ))}
              </ul>

              <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-4">Health & Safety Tips</h3>
              <div className="space-y-3">
                {travelTips.map((tip, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-navy-800 rounded-lg">
                    <div className="w-8 h-8 bg-cyan-100 dark:bg-cyan-900/20 rounded-lg flex items-center justify-center text-cyan-600 dark:text-cyan-400 shrink-0">
                      {tip.icon}
                    </div>
                    <div>
                      <div className="font-bold text-navy-900 dark:text-white text-sm">{tip.tip}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{tip.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When to Book */}
      <section className="py-20 bg-gradient-to-br from-navy-900 to-navy-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Calendar className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              When to Book Your Consultation
            </h2>
            <p className="text-gray-300 text-lg">
              Plan ahead to ensure full protection for your tropical vacation or adventure.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-cyan-400 mb-2">6-8 Weeks</div>
              <div className="text-white font-medium mb-2">Ideal Timeline</div>
              <div className="text-sm text-gray-400">Allows time for multi-dose vaccines and yellow fever if needed</div>
            </div>
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">4 Weeks</div>
              <div className="text-white font-medium mb-2">Standard Planning</div>
              <div className="text-sm text-gray-400">Sufficient for most vaccines and prescriptions</div>
            </div>
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-pink-400 mb-2">Last Minute</div>
              <div className="text-white font-medium mb-2">Still Worthwhile</div>
              <div className="text-sm text-gray-400">Partial protection and important health advice available</div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Book Your Latin America Consultation
              <ArrowRight size={20} />
            </button>
            <p className="text-gray-400 text-sm mt-4">
              Virtual consultations available • $60 CAD • Evening & weekend appointments
            </p>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </div>
  );
};

export default LatinAmerica;
